import React, { ReactElement, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getColorDisplayValue } from '../../../../../common/colors/elementColorFormatUtil';
import { ElementType } from '../../../../../common/elements/elementTypes';
import { ImMNElement } from '../../../../../common/elements/elementModelTypes';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../common/table/tableConstants';
import { getThemeIsDarkMode } from '../../../../theme/themeSelector';
import { SheetId } from '../../../../mobile/structural/sheet/sheetTypes';
import useSheet from '../../../../mobile/structural/sheet/hooks/useSheet';
import { getBackgroundColorHex } from '../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { selectActiveTableCurrentCellBackgroundColor } from '../store/selectors/activeHotTableSelectors';
import { selectActiveTableCellSelection } from '../../bridge/store/selectors/activeHotTableCellSelectionSelectors';
import { useActiveTableCallback, useActiveTableSelector } from '../../bridge/store/hotTableStoreHooks';
import { updateHotTableSelectedCellBackgroundColor } from '../../bridge/utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import { setHotTableCellBackgroundColor } from '../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import ToolbarTool from '../../../../workspace/toolbar/components/ToolbarTool';
import ColorToolIcon from '../../../../workspace/toolbar/components/selectionTools/colorTools/ColorToolIcon';
import MobileColorToolContent from '../../../../workspace/toolbar/components/selectionTools/colorTools/popup/mobile/MobileColorToolContent';

type MobileTableCellColorToolProps = {
    name: string;
    selectedElements: Immutable.List<ImMNElement>;
};

const MobileTableCellColorTool = ({ name, selectedElements }: MobileTableCellColorToolProps): ReactElement => {
    const isDarkMode = useSelector(getThemeIsDarkMode);

    const sheetProps = useSheet(SheetId.ColorTool);

    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);
    const selectedColor = useActiveTableSelector(selectActiveTableCurrentCellBackgroundColor);

    // Temporary picker previews mutate HOT cell meta directly, so we track that state and force the
    // final committed update to persist even when the chosen color matches the already-previewed value.
    const hasTempColorRef = useRef(false);

    const updateCellBackgroundColor = useActiveTableCallback((activeTable, backgroundColor: string | null) => {
        updateHotTableSelectedCellBackgroundColor(activeTable.instance, backgroundColor, hasTempColorRef.current);
        hasTempColorRef.current = false;
    }, []);

    const updateTemporaryCellColor = useActiveTableCallback((activeTable, tempColor: string | null) => {
        setHotTableCellBackgroundColor(activeTable.instance, undefined, tempColor);
        hasTempColorRef.current = true;
    }, []);

    const isDisabled = !currentCellSelections || currentCellSelections.length === 0;
    const selectedColorDisplayValue =
        selectedColor === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR
            ? 'var(--ws-table-header-row-background)'
            : getColorDisplayValue(selectedColor);

    return (
        <>
            <ToolbarTool
                name={name}
                isOpen={sheetProps.isOpen}
                disabled={isDisabled}
                onClick={isDisabled ? undefined : () => sheetProps.openSheet()}
            >
                <ColorToolIcon
                    color={isDisabled ? 'var(--ui-toolbar-tool-disabled-foreground)' : selectedColorDisplayValue}
                    isActive={sheetProps.isOpen}
                />
            </ToolbarTool>
            <MobileColorToolContent
                name={name}
                elementType={ElementType.TABLE_TYPE}
                selectedElements={selectedElements}
                supportBackgroundColor={false}
                selectedColor={selectedColor ?? null}
                selectedColorHex={getBackgroundColorHex(selectedColor, isDarkMode)}
                selectedBackgroundColorName=""
                selectedBackgroundColor={null}
                selectedBackgroundColorHex=""
                customDefaultColors={Object.values(TABLE_DEFAULT_COLORS)}
                customColorHandler={updateCellBackgroundColor}
                customTempColorHandler={updateTemporaryCellColor}
                openSheet={sheetProps.openSheet}
                closeSheet={sheetProps.closeSheet}
                sheetProps={sheetProps}
            />
        </>
    );
};

export default MobileTableCellColorTool;
